// invoice_detail_v1: InvoiceDetail modal — view + status actions + print
// invoice_send_to_chat_v1: + send to chat conversation
import React, { useState, useEffect } from 'react';
import API from '../api';
import {
  X, Printer, Edit2, Send, CheckCircle, XCircle, Trash2,
  Phone, MapPin, Calendar, CreditCard, Wallet, FileText,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_META = {
  draft:     { label: 'ร่าง',     color: '#64748b', bg: '#f1f5f9' },
  sent:      { label: 'ส่งแล้ว',  color: '#0284c7', bg: '#e0f2fe' },
  paid:      { label: 'ชำระแล้ว', color: '#16a34a', bg: '#dcfce7' },
  overdue:   { label: 'ค้างชำระ', color: '#dc2626', bg: '#fee2e2' },
  cancelled: { label: 'ยกเลิก',   color: '#94a3b8', bg: '#f1f5f9' },
};

const PAYMENT_LABEL = {
  promptpay:     { label: 'PromptPay',  icon: '💚' },
  bank_transfer: { label: 'โอนธนาคาร', icon: '🏦' },
  usdt_trc20:    { label: 'USDT TRC20', icon: '🔴' },
  usdt_erc20:    { label: 'USDT ERC20', icon: '🔵' },
  cash:          { label: 'เงินสด',     icon: '💵' },
  other:         { label: 'อื่น ๆ',     icon: '📦' },
};

function formatDateTH(d) {
  if (!d) return '-';
  const date = new Date(d);
  if (isNaN(date)) return '-';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return dd + '/' + mm + '/' + yyyy;
}

function formatDateTimeTH(d) {
  if (!d) return '-';
  const date = new Date(d);
  if (isNaN(date)) return '-';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return dd + '/' + mm + '/' + yyyy + ' ' + hh + ':' + mi;
}

function formatMoney(n, currency) {
  const num = Number(n) || 0;
  const cur = currency || 'THB';
  if (cur === 'USDT') return num.toFixed(2) + ' USDT';
  return num.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ฿';
}

export default function InvoiceDetail({ open, invoiceId, onClose, onChanged, onEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    if (!open || !invoiceId) { setData(null); return; }
    setLoading(true);
    API.get('/api/invoices/' + invoiceId)
      .then(r => setData(r.data))
      .catch(() => { toast.error('โหลดบิลไม่สำเร็จ'); onClose && onClose(); })
      .finally(() => setLoading(false));
  }, [open, invoiceId, onClose]);

  const reload = async () => {
    if (!invoiceId) return;
    try {
      const r = await API.get('/api/invoices/' + invoiceId);
      setData(r.data);
    } catch (e) { /* ignore */ }
  };

  const handleMarkPaid = async () => {
    if (!window.confirm('ยืนยันมาร์คบิลนี้ว่าชำระแล้ว?')) return;
    setBusy(true);
    try {
      await API.patch('/api/invoices/' + invoiceId + '/mark-paid', {});
      toast.success('มาร์คชำระแล้ว ✅');
      await reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'มาร์คชำระไม่สำเร็จ');
    }
    setBusy(false);
  };

  const handleMarkSent = async () => {
    if (!window.confirm('ยืนยันมาร์คบิลนี้ว่าส่งแล้ว?')) return;
    setBusy(true);
    try {
      await API.patch('/api/invoices/' + invoiceId + '/mark-sent', { sent_channel: 'manual' });
      toast.success('มาร์คส่งแล้ว 📤');
      await reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'มาร์คส่งไม่สำเร็จ');
    }
    setBusy(false);
  };

  const handleCancel = async () => {
    if (!window.confirm('ยืนยันยกเลิกบิลนี้?')) return;
    setBusy(true);
    try {
      await API.patch('/api/invoices/' + invoiceId + '/cancel', {});
      toast.success('ยกเลิกบิลแล้ว');
      await reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'ยกเลิกไม่สำเร็จ');
    }
    setBusy(false);
  };

  const handleDelete = async () => {
    if (!data) return;
    if (data.invoice.status === 'paid') {
      toast.error('ไม่สามารถลบบิลที่ชำระแล้ว');
      return;
    }
    if (!window.confirm('ลบบิล ' + data.invoice.invoice_number + ' ?')) return;
    setBusy(true);
    try {
      await API.delete('/api/invoices/' + invoiceId);
      toast.success('ลบบิลสำเร็จ');
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'ลบไม่สำเร็จ');
    }
    setBusy(false);
  };

  const handleEditClick = () => {
    if (!data) return;
    if (data.invoice.status !== 'draft') {
      toast.error('แก้ไขได้เฉพาะบิลร่าง');
      return;
    }
    onEdit && onEdit(data.invoice.id);
    onClose && onClose();
  };

  const handlePrint = () => {
    window.print();
  };

  // invoice_send_to_chat_v1: hooks MUST be before early return
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendConvs, setSendConvs] = useState([]);
  const [sendConvId, setSendConvId] = useState('');
  const [sendMessage, setSendMessage] = useState('');

  if (!open) return null;

  const inv = data ? data.invoice : null;
  const items = data ? (data.items || []) : [];
  const meta = inv ? (STATUS_META[inv.status] || STATUS_META.draft) : STATUS_META.draft;
  const pm = inv && inv.payment_method ? PAYMENT_LABEL[inv.payment_method] : null;

  const handleOpenSend = async () => {
    try {
      setBusy(true);
      // eligible_conversations_v1: use invoice-scoped endpoint (super admin can access)
      const { data } = await API.get('/api/invoices/' + invoiceId + '/eligible-conversations');
      setSendConvs(data || []);
      setSendConvId('');
      setSendMessage('');
      setShowSendModal(true);
    } catch (e) {
      toast.error('โหลดรายการแชทไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!sendConvId) { toast.error('กรุณาเลือกแชทปลายทาง'); return; }
    setBusy(true);
    try {
      await API.post('/api/invoices/' + invoiceId + '/send-to-chat', {
        conversation_id: sendConvId,
        custom_message: sendMessage,
      });
      toast.success('ส่งบิลสำเร็จ 📤');
      setShowSendModal(false);
      const r = await API.get('/api/invoices/' + invoiceId);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'ส่งบิลไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  // invoice_send_to_chat_hidden_v1: temporarily disabled pending redesign (logic uses conversations, should use tenant_customers)
  const canSendToChat = false; const _canSendToChat_orig = inv && inv.status !== 'cancelled';

  const canEdit = inv && inv.status === 'draft';
  const canMarkSent = inv && ['draft', 'sent', 'overdue'].includes(inv.status);
  const canMarkPaid = inv && ['draft', 'sent', 'overdue'].includes(inv.status);
  const canCancel = inv && inv.status !== 'paid' && inv.status !== 'cancelled';
  const canDelete = inv && inv.status !== 'paid';

  return (
    <div onClick={onClose}
      style={{
        position:'fixed', inset:0, background:'rgba(15,23,42,0.6)',
        display:'flex', alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent:'center', zIndex: 1000,
        padding: isMobile ? 0 : 16, overflowY:'auto',
      }}>
      <div onClick={e => e.stopPropagation()}
        className="invoice-detail-modal"
        style={{
          background:'#fff', borderRadius: isMobile ? 0 : 14,
          width:'100%', maxWidth: 800,
          maxHeight: isMobile ? '100vh' : '92vh',
          display:'flex', flexDirection:'column',
          boxShadow:'0 20px 50px rgba(0,0,0,0.25)',
        }}>
        {/* Header — sticky */}
        <div style={{
          padding: isMobile ? '14px 16px' : '18px 24px',
          borderBottom:'1px solid #e2e8f0',
          display:'flex', alignItems:'center', justifyContent:'space-between',
          position:'sticky', top: 0, background:'#fff', zIndex: 2,
          borderRadius: isMobile ? 0 : '14px 14px 0 0',
        }}>
          <h2 style={{ margin:0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color:'#0f172a' }}>
            <FileText size={18} style={{ display:'inline', verticalAlign:'middle', marginRight: 6 }} />
            รายละเอียดบิล
          </h2>
          <div style={{ display:'flex', gap: 8 }}>
            <button onClick={handlePrint} title="พิมพ์" className="no-print"
              style={{
                background:'#f1f5f9', border:'none', borderRadius: 8,
                padding: 8, cursor:'pointer', color:'#475569',
              }}>
              <Printer size={16} />
            </button>
            <button onClick={onClose} title="ปิด" className="no-print"
              style={{ background:'none', border:'none', cursor:'pointer', padding: 6 }}>
              <X size={22} color="#94a3b8" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? 14 : 24, overflowY:'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign:'center', color:'#94a3b8' }}>
              กำลังโหลด...
            </div>
          ) : !inv ? (
            <div style={{ padding: 40, textAlign:'center', color:'#94a3b8' }}>
              ไม่พบข้อมูลบิล
            </div>
          ) : (
            <>
              {/* Invoice number + status */}
              <div style={{
                display:'flex', justifyContent:'space-between', alignItems:'center',
                marginBottom: 18, paddingBottom: 14, borderBottom:'2px solid #f1f5f9',
                flexWrap:'wrap', gap: 12,
              }}>
                <div>
                  <div style={{ fontSize: 11, color:'#94a3b8', fontWeight:600, letterSpacing:'0.05em' }}>เลขที่บิล</div>
                  <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color:'#0ea5e9', fontFamily:'monospace' }}>
                    {inv.invoice_number}
                  </div>
                </div>
                <span style={{
                  background: meta.bg, color: meta.color,
                  borderRadius: 8, padding: '6px 14px',
                  fontSize: 13, fontWeight: 700,
                }}>
                  {meta.label}
                </span>
              </div>

              {/* Dates grid */}
              <div style={{
                display:'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: 12, marginBottom: 18,
              }}>
                <InfoBox icon={Calendar} label="วันที่ออก" value={formatDateTH(inv.issue_date)} />
                <InfoBox icon={Calendar} label="กำหนดชำระ"
                  value={formatDateTH(inv.due_date)}
                  highlight={inv.status === 'overdue' ? '#dc2626' : null} />
              </div>

              {/* Recipient */}
              <SectionTitle>📇 ผู้รับ</SectionTitle>
              <div style={{
                background:'#f8fafc', borderRadius: 10, padding: 14, marginBottom: 18,
              }}>
                <div style={{ fontWeight: 700, fontSize: 15, color:'#0f172a', marginBottom: 6 }}>
                  {inv.recipient_name || '-'}
                </div>
                {inv.recipient_phone && (
                  <div style={{ fontSize: 13, color:'#64748b', display:'flex', alignItems:'center', gap: 6, marginBottom: 4 }}>
                    <Phone size={13} />{inv.recipient_phone}
                  </div>
                )}
                {inv.recipient_address && (
                  <div style={{ fontSize: 13, color:'#64748b', display:'flex', alignItems:'flex-start', gap: 6 }}>
                    <MapPin size={13} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ whiteSpace:'pre-wrap' }}>{inv.recipient_address}</span>
                  </div>
                )}
              </div>

              {/* Items table */}
              <SectionTitle>📦 รายการ ({items.length})</SectionTitle>
              <div style={{
                background:'#fff', border:'1px solid #e2e8f0', borderRadius: 10,
                overflow:'hidden', marginBottom: 14,
              }}>
                {/* Header row */}
                <div style={{
                  display:'grid',
                  gridTemplateColumns: isMobile ? '40px 1fr 80px' : '40px 1fr 80px 100px 110px',
                  background:'#f8fafc', borderBottom:'1px solid #e2e8f0',
                  padding: '10px 12px', fontSize: 11, fontWeight: 600,
                  color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em',
                }}>
                  <div>#</div>
                  <div>รายละเอียด</div>
                  <div style={{ textAlign:'right' }}>{isMobile ? 'รวม' : 'จำนวน'}</div>
                  {!isMobile && <div style={{ textAlign:'right' }}>ราคา/หน่วย</div>}
                  {!isMobile && <div style={{ textAlign:'right' }}>รวม</div>}
                </div>
                {/* Rows */}
                {items.length === 0 ? (
                  <div style={{ padding: 20, textAlign:'center', color:'#94a3b8', fontSize: 13 }}>
                    ไม่มีรายการ
                  </div>
                ) : items.map((it, idx) => (
                  <div key={it.id || idx} style={{
                    display:'grid',
                    gridTemplateColumns: isMobile ? '40px 1fr 80px' : '40px 1fr 80px 100px 110px',
                    padding: '10px 12px', fontSize: 13,
                    borderBottom: idx === items.length - 1 ? 'none' : '1px solid #f1f5f9',
                    alignItems:'center',
                  }}>
                    <div style={{ color:'#94a3b8', fontWeight: 600 }}>{idx + 1}</div>
                    <div style={{ color:'#1e293b' }}>
                      {it.description}
                      {isMobile && (
                        <div style={{ fontSize: 11, color:'#94a3b8', marginTop: 2 }}>
                          {Number(it.quantity).toLocaleString('th-TH')} × {Number(it.unit_price).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                        </div>
                      )}
                    </div>
                    {isMobile ? (
                      <div style={{ textAlign:'right', fontWeight: 600, color:'#0284c7' }}>
                        {Number(it.line_total).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </div>
                    ) : (
                      <>
                        <div style={{ textAlign:'right', color:'#475569' }}>
                          {Number(it.quantity).toLocaleString('th-TH')}
                        </div>
                        <div style={{ textAlign:'right', color:'#475569' }}>
                          {Number(it.unit_price).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                        </div>
                        <div style={{ textAlign:'right', fontWeight: 700, color:'#0284c7' }}>
                          {Number(it.line_total).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Total */}
              <div style={{
                background:'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                color:'#fff', borderRadius: 10, padding: isMobile ? 16 : 20,
                marginBottom: 18,
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, opacity: 0.95 }}>ยอดรวมทั้งหมด</span>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, lineHeight: 1.1 }}>
                      {formatMoney(inv.total_amount, inv.currency)}
                    </div>
                    {inv.total_amount_usdt != null && inv.currency !== 'USDT' && (
                      <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                        ≈ {Number(inv.total_amount_usdt).toFixed(2)} USDT
                        {inv.usdt_rate && ' (' + Number(inv.usdt_rate).toFixed(2) + ' THB/USDT)'}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment method */}
              {pm && (
                <>
                  <SectionTitle>💳 วิธีรับเงิน</SectionTitle>
                  <div style={{
                    background:'#f8fafc', borderRadius: 10, padding: 14, marginBottom: 14,
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: inv.crypto_wallet_address ? 8 : 0 }}>
                      <span style={{ fontSize: 22 }}>{pm.icon}</span>
                      <span style={{ fontSize: 14, fontWeight: 600, color:'#0f172a' }}>{pm.label}</span>
                    </div>
                    {inv.crypto_wallet_address && (
                      <div style={{
                        background:'#fff', borderRadius: 8, padding: '8px 12px',
                        border:'1px solid #e2e8f0', fontSize: 12,
                        fontFamily:'monospace', wordBreak:'break-all',
                        color:'#475569',
                      }}>
                        <Wallet size={11} style={{ display:'inline', verticalAlign:'middle', marginRight: 4 }} />
                        {inv.crypto_wallet_address}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Note */}
              {inv.note && (
                <>
                  <SectionTitle>📝 หมายเหตุ</SectionTitle>
                  <div style={{
                    background:'#fffbeb', border:'1px solid #fde68a',
                    borderRadius: 10, padding: 12, marginBottom: 14,
                    fontSize: 13, color:'#78350f', whiteSpace:'pre-wrap',
                  }}>
                    {inv.note}
                  </div>
                </>
              )}

              {/* Paid info (if paid) */}
              {inv.status === 'paid' && (
                <>
                  <SectionTitle>✅ ข้อมูลการชำระ</SectionTitle>
                  <div style={{
                    background:'#dcfce7', borderRadius: 10, padding: 14,
                    border:'1px solid #86efac', marginBottom: 14,
                  }}>
                    <DetailRow label="วันที่ชำระ" value={formatDateTimeTH(inv.paid_at)} />
                    {inv.paid_method && <DetailRow label="วิธีชำระ" value={inv.paid_method} />}
                    {inv.paid_reference && <DetailRow label="อ้างอิง" value={inv.paid_reference} />}
                  </div>
                </>
              )}

              {/* Sent info */}
              {inv.sent_at && inv.status !== 'paid' && (
                <div style={{
                  background:'#e0f2fe', borderRadius: 8, padding: 10,
                  fontSize: 12, color:'#0c4a6e', marginBottom: 14,
                }}>
                  📤 ส่งบิลเมื่อ {formatDateTimeTH(inv.sent_at)}
                  {inv.sent_channel && ' ผ่าน ' + inv.sent_channel}
                </div>
              )}

              {/* Meta footer */}
              <div style={{
                fontSize: 11, color:'#94a3b8', textAlign:'center',
                paddingTop: 12, borderTop: '1px solid #f1f5f9',
              }}>
                สร้างเมื่อ {formatDateTimeTH(inv.created_at)} • อัปเดตล่าสุด {formatDateTimeTH(inv.updated_at)}
              </div>
            </>
          )}
        </div>

        {/* Action footer — sticky */}
        {inv && (
          <div className="no-print"
            style={{
              padding: isMobile ? '12px 14px' : '14px 24px',
              borderTop:'1px solid #e2e8f0', background:'#f8fafc',
              display:'flex', gap: 8, flexWrap:'wrap',
              position:'sticky', bottom: 0, zIndex: 2,
              borderRadius: isMobile ? 0 : '0 0 14px 14px',
              justifyContent: isMobile ? 'stretch' : 'flex-end',
            }}>
            {canEdit && (
              <ActionBtn icon={Edit2} label="แก้ไข" onClick={handleEditClick} disabled={busy}
                bg="#f1f5f9" color="#475569" />
            )}
            {canMarkSent && inv.status === 'draft' && (
              <ActionBtn icon={Send} label="มาร์คส่งแล้ว" onClick={handleMarkSent} disabled={busy}
                bg="#e0f2fe" color="#0284c7" />
            )}
            {/* invoice_send_to_chat_v1 */}
            {canSendToChat && (
              <ActionBtn icon={Send} label="ส่งผ่านแชท" onClick={handleOpenSend} disabled={busy}
                bg="#dbeafe" color="#1d4ed8" primary />
            )}
            {canMarkPaid && (
              <ActionBtn icon={CheckCircle} label="มาร์คชำระแล้ว" onClick={handleMarkPaid} disabled={busy}
                bg="#dcfce7" color="#16a34a" primary />
            )}
            {canCancel && (
              <ActionBtn icon={XCircle} label="ยกเลิก" onClick={handleCancel} disabled={busy}
                bg="#f1f5f9" color="#94a3b8" />
            )}
            {canDelete && (
              <ActionBtn icon={Trash2} label="ลบ" onClick={handleDelete} disabled={busy}
                bg="#fff1f2" color="#ef4444" />
            )}
            {!canEdit && !canMarkPaid && !canCancel && !canDelete && (
              <div style={{ fontSize: 12, color:'#94a3b8', padding: '8px 12px' }}>
                บิลนี้ปิดสถานะแล้ว — ดูได้อย่างเดียว
              </div>
            )}
          </div>
        )}

        {/* invoice_send_to_chat_v1: nested modal */}
        {showSendModal && (
          <div onClick={() => !busy && setShowSendModal(false)}
            style={{ position:'fixed', inset: 0, background:'rgba(0,0,0,0.5)',
                     zIndex: 2000, display:'flex', alignItems:'center', justifyContent:'center',
                     padding: 16 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background:'white', borderRadius: 14, maxWidth: 500, width:'100%',
                       padding: 20, maxHeight:'90vh', overflowY:'auto' }}>
              <h3 style={{ margin:'0 0 4px', fontSize: 18, fontWeight: 600 }}>📤 ส่งบิลผ่านแชท</h3>
              <p style={{ margin:'0 0 16px', fontSize: 13, color:'#64748b' }}>
                {inv.invoice_number} · ยอด {Number(inv.total_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })} {inv.currency}
              </p>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display:'block', fontSize: 13, color:'#475569', marginBottom: 6 }}>
                  เลือกแชทปลายทาง <span style={{ color:'#ef4444' }}>*</span>
                </label>
                <select value={sendConvId} onChange={e => setSendConvId(e.target.value)} disabled={busy}
                  style={{ width:'100%', padding: '10px 12px', borderRadius: 8, border:'1px solid #e2e8f0', fontSize: 14, fontFamily:'inherit' }}>
                  <option value="">-- เลือก conversation --</option>
                  {sendConvs.map(c => (
                    <option key={c.id} value={c.id}>
                      {(c.customer_name || '(ไม่มีชื่อ)')} — {(c.channel_name || c.channel_type)} · {c.channel_type}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: 11, color:'#94a3b8', margin:'4px 0 0' }}>
                  เฉพาะ conversation ที่ status = open ({sendConvs.length} รายการ)
                </p>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display:'block', fontSize: 13, color:'#475569', marginBottom: 6 }}>
                  ข้อความเพิ่มเติม (optional)
                </label>
                <textarea value={sendMessage} onChange={e => setSendMessage(e.target.value)} disabled={busy}
                  placeholder="เช่น: ชำระภายใน 3 วันค่ะ ขอบคุณ" rows={3}
                  style={{ width:'100%', padding: '10px 12px', borderRadius: 8, border:'1px solid #e2e8f0', fontSize: 14, fontFamily:'inherit', resize:'vertical' }} />
              </div>
              <div style={{ background:'#f8fafc', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 12, color:'#64748b' }}>
                💡 ระบบจะส่งข้อความบิล (พร้อมรายการ + ยอดรวม + ครบกำหนด) ไปยังแชทที่เลือก
              </div>
              <div style={{ display:'flex', gap: 8, justifyContent:'flex-end' }}>
                <button onClick={() => setShowSendModal(false)} disabled={busy}
                  style={{ padding: '10px 18px', borderRadius: 8, border:'1px solid #e2e8f0', background:'white', cursor: busy ? 'not-allowed' : 'pointer', fontSize: 14, color:'#475569' }}>
                  ยกเลิก
                </button>
                <button onClick={handleSendInvoice} disabled={busy || !sendConvId}
                  style={{ padding: '10px 18px', borderRadius: 8, border:'none',
                           background: (busy || !sendConvId) ? '#cbd5e1' : '#1d4ed8',
                           color:'white', cursor: (busy || !sendConvId) ? 'not-allowed' : 'pointer',
                           fontSize: 14, fontWeight: 600 }}>
                  {busy ? 'กำลังส่ง...' : '📤 ส่งบิล'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- subcomponents ----------
function InfoBox({ icon: Icon, label, value, highlight }) {
  return (
    <div style={{
      background:'#f8fafc', borderRadius: 10, padding: 12,
      display:'flex', alignItems:'center', gap: 10,
    }}>
      <Icon size={18} color="#94a3b8" />
      <div>
        <div style={{ fontSize: 11, color:'#64748b' }}>{label}</div>
        <div style={{
          fontSize: 14, fontWeight: 600,
          color: highlight || '#1e293b', marginTop: 2,
        }}>{value}</div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{
      display:'flex', justifyContent:'space-between',
      padding: '4px 0', fontSize: 13,
    }}>
      <span style={{ color:'#16a34a', fontWeight: 500 }}>{label}:</span>
      <span style={{ color:'#0f172a', fontWeight: 600, fontFamily:'monospace' }}>{value}</span>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, disabled, bg, color, primary }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        padding: '10px 16px', borderRadius: 8,
        background: bg, color, border: primary ? '2px solid ' + color : 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontSize: 13, fontWeight: primary ? 700 : 600,
        display:'inline-flex', alignItems:'center', gap: 6,
        flex: '0 1 auto',
      }}>
      <Icon size={14} />{label}
    </button>
  );
}

const SectionTitle = ({ children }) => (
  <div style={{
    fontSize: 13, fontWeight: 700, color:'#0f172a',
    marginBottom: 8, paddingBottom: 4,
  }}>{children}</div>
);
